import { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useSystemClassifierItems } from '../../configuration';
import {
  getDayjs,
  getDuration,
  getDayjsMonths,
  getDayjsWeekdays,
  getDayjsWeekdaysShort,
  ISO_DATE_FORMAT,
  ISODate,
} from '../utils';
import { Holiday, Month, MonthIndex, MONTHS, Week } from './types';

export function monthWeeks(year: number, month: MonthIndex): Month {
  const weeks: Week[] = [];
  let week: Week = [];
  let date = getDayjs(new Date(year, month, 1))!.startOf('isoWeek');
  const end = date.endOf('isoWeek').endOf('month');

  do {
    for (let i = 0; i < 7; i++) {
      week.push({
        date: date,
        dateStr: date.format(ISO_DATE_FORMAT) as ISODate,
        isCurrentMonth: month === date.month(),
        isHoliday: i >= 5,
      });
      date = date.add(1, 'day');
    }
    weeks.push(week);
    week = [];
  } while (date.isSameOrBefore(end));

  return { month, name: getMonthNames()[month], weeks };
}

export function yearCalendar(year: number): Month[] {
  return MONTHS.map((month) => monthWeeks(year, month));
}

export function weekOfMonth(date: Dayjs) {
  const firstOfMonth = getDayjs(new Date(date.year(), date.month(), 1))!;
  const firstOfMonthWeek = firstOfMonth.startOf('isoWeek');
  const daysBeforeStart = getDuration(
    firstOfMonth.diff(firstOfMonthWeek),
  ).days();
  return Math.floor((date.date() - 1 + daysBeforeStart) / 7);
}

export function findMonthDay(date: Dayjs, month: Month) {
  return month.weeks[weekOfMonth(date)][date.isoWeekday() - 1];
}

export function getMonthNames() {
  return getDayjsMonths();
}

export function getWeekdayNames() {
  return getDayjsWeekdays();
}

export function getWeekdayAbbreviations() {
  return getDayjsWeekdaysShort();
}

export function getCurrentDecadeYears(year: number) {
  const startOfDecade = Math.floor(year / 10) * 10;
  return Array.from(Array(10).keys(), (y) => y + startOfDecade);
}

export function useHolidays() {
  const items = useSystemClassifierItems('HOLIDAY');

  const holidays: Holiday[] = useMemo(
    () =>
      items
        .map((item) => item.code)
        .map((holiday) => {
          const [h1, h2, h3] = holiday.split('-');
          if (h3) {
            return {
              month: (Number(h2) - 1) as MonthIndex,
              dayOfMonth: Number(h3),
              year: Number(h1),
            };
          }
          return {
            month: (Number(h1) - 1) as MonthIndex,
            dayOfMonth: Number(h2),
          };
        }),
    [items],
  );

  const isHoliday = useCallback(
    (date: Dayjs) =>
      holidays.some(
        (h) =>
          h.month === date.month() &&
          h.dayOfMonth === date.date() &&
          (h.year === undefined || h.year === date.year()),
      ),
    [holidays],
  );

  return { holidays, isHoliday };
}
